import { useState, useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useUTSLocaleContext } from "context/utsLocaleContext";
import { CampaignContext } from "context/campaignContext";

import { CampaignProviderProps, PromoCampaign } from "./CampaignProvider.types";
import { findActiveCampaigns } from "./utils";

let CMSPromoCampaings: PromoCampaign[] = [];
try {
    // Important: do not replace with `import`! Import can't handle non-existing files
    CMSPromoCampaings =
        require("../../../cms-data/cms-promo-campaigns.json") as unknown as PromoCampaign[];
} catch {
    // Missing file (e.g. before first fetch); same as empty array
}

export const CampaignProvider = ({
    children,
    campaigns: campaignsFromProps = [],
}: CampaignProviderProps) => {
    const { asPath, locale: routerLocale } = useRouter();
    const { locale } = useUTSLocaleContext();
    const { t } = useTranslation(locale);

    const [activeCampaign, setActiveCampaign] = useState<PromoCampaign | null>(
        () => {
            return findActiveCampaigns(
                Array.isArray(campaignsFromProps) &&
                    campaignsFromProps.length > 0
                    ? campaignsFromProps
                    : CMSPromoCampaings,
                `/${routerLocale}${asPath}`,
                locale
            );
        }
    );

    useEffect(() => {
        const active = findActiveCampaigns(
            Array.isArray(campaignsFromProps) && campaignsFromProps.length > 0
                ? campaignsFromProps
                : CMSPromoCampaings,
            `/${routerLocale}${asPath}`,
            locale
        );
        setActiveCampaign(active);
    }, [asPath, routerLocale, t, locale, campaignsFromProps]);

    const memorizedCampaign = useMemo(() => {
        return activeCampaign;
    }, [activeCampaign]);

    return (
        <CampaignContext.Provider value={{ campaign: memorizedCampaign }}>
            {children}
        </CampaignContext.Provider>
    );
};
